import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Container,
  CSSReset,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Checkbox,
  HStack,
} from "@chakra-ui/react";
import { kebabCase } from "lodash";
import axios from "axios";
import { RouteComponentProps, withRouter } from "react-router";

interface InteractiveReport {
  id?: string;
  name: string;
  slug: string;
  description: string;
  fileName: string;
  roles: string[];
  dataset: string;
}
interface FormValues {
  name: string;
  slug: string;
  description: string;
  fileName: string;
  marketArrayReportsRole: boolean;
  dataset: string;
}

interface SubmitVariables {
  interactiveReport: InteractiveReport;
  id?: string;
}

interface InteractiveFormProps extends RouteComponentProps {
  interactiveReport?: InteractiveReport;
  mutationFn: any;
}

const getDefaultFormValues = (
  interactiveReport?: InteractiveReport
): FormValues => {
  if (!interactiveReport) {
    return {
      name: "",
      fileName: "",
      slug: "",
      description: "",
      marketArrayReportsRole: false,
      dataset: "",
    };
  }

  const hasMarketArrayReportsRole =
    interactiveReport.roles?.includes("MarketArrayReports");

  return {
    name: interactiveReport.name,
    fileName: interactiveReport.fileName,
    slug: interactiveReport.slug,
    description: interactiveReport.description || "",
    marketArrayReportsRole: hasMarketArrayReportsRole,
    dataset: interactiveReport.dataset,
  };
};

const InteractiveReportForm = (props: InteractiveFormProps): JSX.Element => {
  const { interactiveReport, mutationFn } = props;

  const [file, setFile] = useState<FileList | null>();
  const [formIsSubmitting, setFormIsSubmitting] = useState(false);
  const noFileAssociated = !file?.length && !interactiveReport?.fileName;

  const defaultValues = getDefaultFormValues(interactiveReport);

  const { register, handleSubmit, setValue, getValues, watch } =
    useForm<FormValues>({
      defaultValues,
    });

  const didMount = useRef(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const toast = useToast({ duration: 5000, isClosable: true });

  const name = watch("name");
  const dataset = watch("dataset");

  useEffect(() => {
    if (didMount.current) {
      setValue("slug", kebabCase(getValues("name")));
    }
    didMount.current = true;
  }, [name]);

  const handleFormSubmit = async (formValues: FormValues) => {
    setFormIsSubmitting(true);
    if (noFileAssociated) {
      toast({
        title: "Error",
        description: "Please select a .html file to upload.",
        status: "error",
      });

      setFormIsSubmitting(false);
      return null;
    }

    let submitFileName = interactiveReport?.fileName || "";

    if (file?.length) {
      const formData = new FormData();
      const timestamp = Date.now();
      const fileNameWithTimestamp = `${timestamp}_${file[0].name}`;
      formData.append("file", file[0], fileNameWithTimestamp);

      try {
        const res = await axios.post("/upload/interactive-reports", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        submitFileName = res?.data?.fileName || "";
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to upload file.",
          status: "error",
        });
      }
    }

    if (!submitFileName) {
      toast({
        title: "Error",
        description: "Please select a .html file to upload.",
        status: "error",
      });
      setFormIsSubmitting(false);
      return null;
    }

    const getReportRoles = () => {
      if (dataset === "MarketArray" && formValues.marketArrayReportsRole) {
        return ["MarketArrayReports"];
      }

      if (dataset === "TSIS") {
        return ["TSISForecasts"];
      }

      return [];
    };

    const submitValues: InteractiveReport = {
      fileName: submitFileName,
      name: formValues.name,
      description: formValues.description,
      slug: formValues.slug,
      roles: getReportRoles(),
      dataset: formValues.dataset,
    };

    const submitVariables: SubmitVariables = {
      interactiveReport: submitValues,
    };

    if (interactiveReport?.id) {
      submitVariables.id = interactiveReport.id;
    }

    const { data, error } = await mutationFn({
      variables: submitVariables,
    });

    if (error) {
      toast({
        title: "Error",
        description: "An unknown error occurred.",
        status: "error",
      });
      setFormIsSubmitting(false);
      return;
    }

    if (
      data?.createInteractiveReport?.interactiveReport ||
      data?.updateInteractiveReport?.interactiveReport
    ) {
      toast({
        title: "Success",
        description:
          data?.createInteractiveReport?.message ||
          data?.updateInteractiveReport?.message,
        status: "success",
      });

      if (data?.createInteractiveReport?.interactiveReport?.id) {
        props.history.push(
          `/interactive-reports/${data?.createInteractiveReport?.interactiveReport?.id}/edit`
        );
      }

      if (data?.updateInteractiveReport?.interactiveReport?.id) {
        props.history.push("/interactive-reports");
      }

      return;
    }

    toast({
      title: "Error",
      description:
        data?.createInteractiveReport?.message ||
        data?.updateInteractiveReport?.message ||
        "An unknown error occurred.",
      status: "error",
    });

    setFormIsSubmitting(false);
  };

  return (
    <Container maxW={1000}>
      <CSSReset />
      <Box
        borderWidth={{ lg: 1 }}
        p={{ lg: 5 }}
        borderRadius={{ lg: 5 }}
        shadow={{ lg: "lg" }}
      >
        <Heading>Interactive Report Form</Heading>
        <form
          style={{ marginTop: 15 }}
          onSubmit={handleSubmit(handleFormSubmit)}
          noValidate
        >
          <FormControl isRequired mb={4}>
            <FormLabel htmlFor="name" mb={1}>
              Name
            </FormLabel>
            <Input
              id="name"
              data-cy="name-input"
              {...register("name", {
                required: true,
              })}
            />
          </FormControl>

          <FormControl isRequired mb={4}>
            <FormLabel htmlFor="slug" mb={1}>
              Slug
            </FormLabel>
            <Input
              id="slug"
              data-cy="slug-input"
              {...register("slug", {
                required: true,
              })}
            />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel htmlFor="dataset" mb={1}>
              Dataset
            </FormLabel>
            <RadioGroup
              id="dataset"
              data-cy="dataset"
              defaultValue={defaultValues.dataset}
              {...register("dataset")}
              onChange={(value) => {
                setValue("dataset", value);
                setValue("marketArrayReportsRole", false);
              }}
            >
              <Stack direction="row" spacing={5}>
                <Radio value="MarketArray" margin={0}>
                  Market Array
                </Radio>
                <Radio value="TSIS">TSIS</Radio>
                <Radio value="AiProductSeries">AI Product Series</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>

          {dataset === "MarketArray" && (
            <FormControl mb={4}>
              <FormLabel htmlFor="marketArrayReportsRole" mb={1}>
                Allow users with Market Array Reports role access to view?
              </FormLabel>
              <Checkbox
                id="marketArrayReportsRole"
                data-cy="add-role-checkbox"
                {...register("marketArrayReportsRole")}
              />
            </FormControl>
          )}

          <FormControl mb={4}>
            <FormLabel htmlFor="description" mb={1}>
              Description
            </FormLabel>
            <Textarea
              data-cy="description-input"
              id="description"
              height="auto"
              minH="100px"
              {...register("description")}
            />
          </FormControl>

          {interactiveReport && (
            <FormControl mb={4} isRequired>
              <FormLabel htmlFor="fileName" mb={1}>
                Interactive Report File
              </FormLabel>
              <Input
                data-cy="attached-file-name-input"
                id="fileName"
                {...register("fileName")}
                disabled
              />
            </FormControl>
          )}

          <FormControl isRequired={!interactiveReport} mb={4}>
            <FormLabel htmlFor="htmlFile" mb={1}>
              Upload File
            </FormLabel>
            <HStack
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
              padding={5}
            >
              <Button
                data-cy="upload-file-button"
                onClick={() => {
                  inputFileRef.current?.click();
                }}
              >
                Select File
              </Button>
              <FormHelperText>
                {file?.[0]?.name || "No file selected"}
              </FormHelperText>
              <Input
                display="none"
                ref={inputFileRef}
                data-cy="upload-file-input"
                id="htmlFile"
                type="file"
                accept={"text/html"}
                border="none"
                padding={"0"}
                height={"fit-content"}
                onChange={(e) => {
                  setFile(e.target.files);
                }}
              />
            </HStack>
          </FormControl>
          <Button
            type="submit"
            colorScheme="blue"
            data-cy="submit-button"
            isLoading={formIsSubmitting}
            loadingText={interactiveReport ? "Updating" : "Creating"}
          >
            {interactiveReport ? "Update" : "Create"}
          </Button>
        </form>
      </Box>
    </Container>
  );
};

export default withRouter(InteractiveReportForm);
